import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, Ban, Trash2, Scale } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════
   BENCHORY — Landing Page · Cabinet de Conseil Premium
   ═══════════════════════════════════════════════════════════════════ */

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cdfce993-612e-4617-ac7b-89da32b19bd7/artifacts/wcwsdulx_Logo%20benchory.png';

const CSS_VARS = `
:root {
  --night: #111622;
  --night2: #181E2C;
  --night3: #212838;
  --gold: #B8924A;
  --gold2: #D4A85C;
  --gold3: #E8C87A;
  --ivory: #FAF8F5;
  --ivory2: #F0EBE2;
  --ink: #0D0D0D;
  --ink2: #2A2A2A;
  --gray: #777777;
  --sep: #E8E0D4;
  --cream: #F4EFE6;
  --serif: 'Playfair Display', Georgia, serif;
  --sans: 'DM Sans', 'Helvetica Neue', sans-serif;
}
.fade-up, .fade-left, .fade-right, .step-card, .plan-card, .srow {
  opacity: 0;
  transition: opacity 0.7s ease, transform 0.7s ease;
}
.fade-up   { transform: translateY(24px); }
.fade-left { transform: translateX(-20px); }
.fade-right{ transform: translateX(20px); }
.step-card { transform: translateY(20px); }
.plan-card { transform: translateY(16px); }
.srow      { transform: translateX(-12px); }
.fade-up.visible, .fade-left.visible, .fade-right.visible,
.step-card.visible, .plan-card.visible, .srow.visible {
  opacity: 1; transform: none;
}
.d1 { transition-delay: 0.1s; }
.d2 { transition-delay: 0.2s; }
.d3 { transition-delay: 0.3s; }
.d4 { transition-delay: 0.4s; }
.d5 { transition-delay: 0.5s; }
.hero-el { opacity: 0; transform: translateY(20px); transition: opacity 0.8s ease, transform 0.8s ease; }
.hero-el.visible { opacity: 1; transform: none; }
@keyframes ticker-scroll {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}
.faq-answer {
  max-height: 0; overflow: hidden; transition: max-height 0.4s ease, padding 0.3s ease;
}
.faq-answer.open {
  max-height: 300px; padding-bottom: 24px;
}
.faq-icon { transition: transform 0.3s ease; }
.faq-icon.open { transform: rotate(45deg); }
/* Hero logo entrance */
@keyframes heroLogoIn {
  0%   { opacity: 0; transform: scale(0.85); filter: blur(6px); }
  50%  { opacity: 0.6; transform: scale(0.97); filter: blur(1px); }
  100% { opacity: 1; transform: scale(1); filter: blur(0); }
}
.hero-logo-anim {
  opacity: 0;
  animation: heroLogoIn 2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
/* Gold line grow */
@keyframes lineGrow {
  from { width: 0; }
  to   { width: 48px; }
}
.hero-line-anim {
  width: 0;
  animation: lineGrow 0.8s ease forwards;
}
`;

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Counter animation */
function animCount(el, target, suffix, duration) {
  if (!el) return;
  let start = null;
  const step = (ts) => {
    if (!start) start = ts;
    const prog = Math.min((ts - start) / duration, 1);
    const ease = 1 - Math.pow(1 - prog, 3);
    el.textContent = Math.round(ease * target) + suffix;
    if (prog < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

/* ── Navbar ── */
function Navbar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  return (
    <nav data-testid="navbar" style={{
      position: 'sticky', top: 0, zIndex: 999, height: 76,
      background: scrolled ? 'rgba(255,255,255,0.97)' : 'rgba(255,255,255,0.97)',
      backdropFilter: scrolled ? 'blur(12px)' : 'none',
      borderBottom: '1px solid #ECE8DF',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '0 64px', fontFamily: 'var(--sans)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
        <img src={LOGO_URL} alt="BENCHORY" style={{ height: 34, objectFit: 'contain' }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 40 }}>
        {['Notre approche', 'Tarifs', 'Contact'].map(l => (
          <a key={l} href={`#${l.toLowerCase().replace(/\s/g, '-').replace(/è/g, 'e')}`} style={{
            fontSize: 11, letterSpacing: 2.5, textTransform: 'uppercase', color: '#777', textDecoration: 'none', fontWeight: 400, transition: 'color 0.25s ease',
          }} onMouseEnter={e => e.target.style.color = 'var(--gold)'} onMouseLeave={e => e.target.style.color = '#777'}>{l}</a>
        ))}
        <span style={{ fontSize: 11, letterSpacing: 2.5, color: '#777', cursor: 'pointer', transition: 'color 0.25s ease' }}
          onClick={() => navigate('/login')}
          onMouseEnter={e => e.target.style.color = 'var(--gold)'} onMouseLeave={e => e.target.style.color = '#777'}>
          Connexion
        </span>
        <button data-testid="navbar-cta" onClick={() => navigate('/inscription')} style={{
          fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 500, letterSpacing: 3, textTransform: 'uppercase',
          background: 'var(--night)', color: 'white', border: 'none', padding: '12px 28px', cursor: 'pointer', transition: 'background 0.25s ease',
        }} onMouseEnter={e => e.target.style.background = 'var(--gold)'} onMouseLeave={e => e.target.style.background = 'var(--night)'}>
          Démarrer
        </button>
      </div>
    </nav>
  );
}

/* ── Hero ── */
function Hero() {
  const navigate = useNavigate();
  const b1 = useRef(null);
  const b3 = useRef(null);
  const [logoVisible, setLogoVisible] = useState(false);

  useEffect(() => {
    // Logo appears first with delay
    setTimeout(() => setLogoVisible(true), 800);
    const ids = ['htag','htitle','hsub','hctas','hnote','hbento'];
    const delays = [2200, 2500, 2700, 2900, 3000, 2400];
    ids.forEach((id, i) => {
      setTimeout(() => document.getElementById(id)?.classList.add('visible'), delays[i]);
    });
    setTimeout(() => {
      animCount(b1.current, 18, '', 700);
      animCount(b3.current, 100, '%', 900);
    }, 2800);
  }, []);

  return (
    <section data-testid="hero-section" style={{
      minHeight: '100vh', background: 'var(--night)', display: 'grid',
      gridTemplateColumns: '1.1fr 0.9fr', gap: 60, padding: '80px 64px 64px',
      alignItems: 'center', position: 'relative', overflow: 'hidden',
    }}>
      {/* Decorative grid dots */}
      <div style={{ position: 'absolute', top: 40, right: 80, display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 18, opacity: 0.12 }}>
        {Array(40).fill(0).map((_, i) => <div key={i} style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--gold)' }} />)}
      </div>
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: '50%', width: 1, background: 'rgba(255,255,255,0.04)' }} />

      {/* Left column */}
      <div style={{ maxWidth: 600, position: 'relative', zIndex: 1 }}>
        {/* Animated logo entrance — centered, large */}
        <div style={{ marginBottom: 36, display: 'flex', justifyContent: 'center' }}>
          {logoVisible && (
            <img src={LOGO_URL} alt="BENCHORY" className="hero-logo-anim" style={{ height: 72, objectFit: 'contain' }} />
          )}
        </div>

        <div id="htag" className="hero-el" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
          <span style={{ fontFamily: 'var(--sans)', fontSize: 9, letterSpacing: 4, textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 400, lineHeight: 1.6 }}>
            Le premier outil de pilotage mensuel conçu pour les dirigeants de PME et commerces indépendants
          </span>
        </div>
        <h1 id="htitle" className="hero-el" style={{ fontFamily: 'var(--serif)', fontSize: 60, fontWeight: 400, color: '#F4EFE6', lineHeight: 1.07, marginBottom: 28 }}>
          Vous dirigez avec votre instinct.<br />
          <em style={{ color: 'var(--gold2)', fontStyle: 'italic' }}>Imaginez piloter avec des certitudes.</em>
        </h1>
        <p id="hsub" className="hero-el" style={{ fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 300, color: 'rgba(244,239,230,0.45)', lineHeight: 1.9, maxWidth: 480, marginBottom: 36 }}>
          Chaque mois, BENCHORY analyse vos indicateurs financiers, les confronte à ceux d'établissements comparables et vous révèle, en euros, les leviers à activer pour améliorer votre rentabilité.
        </p>
        <div id="hctas" className="hero-el" style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 32 }}>
          <button data-testid="hero-cta-main" onClick={() => navigate('/inscription')} style={{
            fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 500, letterSpacing: 3, textTransform: 'uppercase',
            background: 'var(--gold2)', color: 'var(--night)', border: 'none', padding: '17px 40px', cursor: 'pointer', transition: 'background 0.25s ease',
          }} onMouseEnter={e => e.target.style.background = 'var(--gold3)'} onMouseLeave={e => e.target.style.background = 'var(--gold2)'}>
            Obtenir mon premier diagnostic
          </button>
          <a href="#notre-approche" style={{ fontFamily: 'var(--sans)', fontSize: 12, color: 'rgba(244,239,230,0.3)', textDecoration: 'none', transition: 'color 0.25s ease' }}
            onMouseEnter={e => e.target.style.color = 'var(--gold)'} onMouseLeave={e => e.target.style.color = 'rgba(244,239,230,0.3)'}>
            Découvrir l'approche →
          </a>
        </div>
        <p id="hnote" className="hero-el" style={{ fontFamily: 'var(--sans)', fontSize: 9, color: 'rgba(244,239,230,0.18)', letterSpacing: 2.5, textTransform: 'uppercase' }}>
          Premier mois offert · Résiliation libre
        </p>
      </div>

      {/* Right column — bento panel */}
      <div id="hbento" className="hero-el" style={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'relative', zIndex: 1 }}>
        <div style={{ background: 'rgba(180,146,74,0.06)', border: '1px solid rgba(180,146,74,0.2)', borderLeft: '2px solid var(--gold)', padding: '40px 36px', flex: 2 }}>
          <span style={{ fontFamily: 'var(--sans)', fontSize: 9, letterSpacing: 4, textTransform: 'uppercase', color: 'var(--gold)', fontWeight: 400 }}>Jusqu'à 18 indicateurs analysés chaque mois</span>
          <div ref={b1} style={{ fontFamily: 'var(--serif)', fontSize: 72, fontWeight: 400, color: 'var(--gold2)', lineHeight: 1, margin: '16px 0 12px' }}>0</div>
          <p style={{ fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 300, color: 'rgba(244,239,230,0.35)', lineHeight: 1.7 }}>
            Jusqu'à dix-huit indicateurs calculés, comparés et traduits en euros. Aucun logiciel de caisse requis.
          </p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderLeft: '2px solid var(--gold)', padding: '28px 24px' }}>
            <span style={{ fontFamily: 'var(--sans)', fontSize: 9, letterSpacing: 4, textTransform: 'uppercase', color: 'var(--gold)' }}>Temps de saisie mensuel</span>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 52, fontWeight: 400, color: 'var(--gold2)', lineHeight: 1, margin: '12px 0 8px' }}>8 min</div>
            <p style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 300, color: 'rgba(244,239,230,0.3)', lineHeight: 1.6 }}>Des données que vous avez déjà dans votre comptabilité</p>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderLeft: '2px solid var(--gold)', padding: '28px 24px' }}>
            <span style={{ fontFamily: 'var(--sans)', fontSize: 9, letterSpacing: 4, textTransform: 'uppercase', color: 'var(--gold)' }}>Confidentialité des données</span>
            <div ref={b3} style={{ fontFamily: 'var(--serif)', fontSize: 52, fontWeight: 400, color: 'var(--gold2)', lineHeight: 1, margin: '12px 0 8px' }}>0%</div>
            <p style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 300, color: 'rgba(244,239,230,0.3)', lineHeight: 1.6 }}>Vos chiffres sont anonymisés. Jamais transmis à des tiers.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Ticker ── */
function Ticker() {
  const msgs = [
    'Solution de pilotage mensuel',
    'Diagnostic financier personnalis\u00e9',
    'Indicateurs traduits en euros',
    'Accompagnement mensuel sur mesure',
    '\u00c9tablissements comparables anonymis\u00e9s',
    'Actions concr\u00e8tes \u00e0 trente jours',
    'Premier mois offert',
    'Sans engagement',
  ];
  const content = [...msgs, ...msgs];
  return (
    <div data-testid="ticker" style={{ background: 'var(--gold2)', height: 48, overflow: 'hidden', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
      <div style={{ display: 'flex', gap: 48, animation: 'ticker-scroll 22s linear infinite' }}>
        {content.map((m, i) => (
          <span key={i} style={{ fontFamily: 'var(--sans)', fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', fontWeight: 500, color: 'var(--night)', flexShrink: 0 }}>
            {m}<span style={{ margin: '0 24px', opacity: 0.3 }}>•</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── Conviction ── */
function Conviction() {
  return (
    <section id="notre-approche" data-testid="conviction-section" style={{ background: 'var(--ivory)', padding: '128px 64px', display: 'flex', justifyContent: 'center' }}>
      <div style={{ maxWidth: 760, textAlign: 'center' }}>
        <Eyebrow text="Notre conviction" centered />
        <h2 className="fade-up d1" style={{ fontFamily: 'var(--serif)', fontSize: 48, fontWeight: 400, color: 'var(--ink)', lineHeight: 1.12, marginBottom: 28 }}>
          Votre performance ne se voit pas.<br />
          <em style={{ color: 'var(--gold2)', fontStyle: 'italic' }}>Votre manque à gagner non plus.</em>
        </h2>
        <p className="fade-up d2" style={{ fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 300, color: '#777', lineHeight: 1.9, maxWidth: 620, margin: '0 auto' }}>
          La plupart des dirigeants disposent de chiffres. Très peu savent ce que ces chiffres signifient face au marché. BENCHORY transforme vos données en décisions, en comparant votre réalité à celle d'établissements qui vous ressemblent.
        </p>
      </div>
    </section>
  );
}

/* ── Split (sans / avec) ── */
function Split() {
  const left = [
    'Un bilan annuel livr\u00e9 avec six mois de retard, sans aucun point de comparaison possible.',
    'Vous savez ce que vous avez r\u00e9alis\u00e9. Vous ignorez si c\u2019est insuffisant, suffisant, ou remarquable.',
    'Aucune priorit\u00e9 chiffr\u00e9e. Des millions d\u2019euros pilot\u00e9s \u00e0 l\u2019instinct, chaque jour.',
    'Les d\u00e9rives s\u2019installent silencieusement pendant des mois avant d\u2019\u00eatre visibles.',
  ];
  const right = [
    'Vos indicateurs positionn\u00e9s face \u00e0 des \u00e9tablissements de m\u00eame profil, zone et taille.',
    'Chaque \u00e9cart traduit en impact financier annuel. Des euros, pas des pourcentages abstraits.',
    'Trois actions concr\u00e8tes, calibr\u00e9es sur votre r\u00e9alit\u00e9, \u00e0 engager dans les trente jours.',
    'Une analyse qui se souvient de vous, \u00e9volue avec vous, et ne r\u00e9p\u00e8te pas ce qui n\u2019a pas fonctionn\u00e9.',
  ];

  return (
    <section data-testid="split-section" style={{ background: 'var(--night2)', display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 500 }}>
      <div style={{ padding: '88px 64px', borderRight: '1px solid rgba(255,255,255,0.05)' }}>
        <span style={{ fontFamily: 'var(--sans)', fontSize: 8, letterSpacing: 5, textTransform: 'uppercase', color: 'rgba(255,255,255,0.18)' }}>Sans BENCHORY</span>
        <h3 className="fade-up" style={{ fontFamily: 'var(--serif)', fontSize: 32, fontWeight: 400, color: 'rgba(255,255,255,0.4)', lineHeight: 1.25, margin: '24px 0 36px' }}>
          Des chiffres sans contexte. Des décisions à l'aveugle.
        </h3>
        {left.map((t, i) => (
          <div key={i} className={`srow d${i + 1}`} style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
            <div style={{ width: 1, minHeight: '100%', background: 'rgba(255,255,255,0.12)', flexShrink: 0 }} />
            <p style={{ fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 300, color: 'rgba(255,255,255,0.3)', lineHeight: 1.7 }}>{t}</p>
          </div>
        ))}
      </div>
      <div style={{ padding: '88px 64px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ width: 18, height: 1, background: 'var(--gold)', opacity: 0.6, display: 'inline-block' }} />
          <span style={{ fontFamily: 'var(--sans)', fontSize: 8, letterSpacing: 5, textTransform: 'uppercase', color: 'var(--gold)' }}>Avec BENCHORY</span>
        </div>
        <h3 className="fade-right" style={{ fontFamily: 'var(--serif)', fontSize: 32, fontWeight: 400, color: '#F4EFE6', lineHeight: 1.25, margin: '24px 0 36px' }}>
          Une lecture claire, comparative, chiffrée. Chaque mois, sans attendre.
        </h3>
        {right.map((t, i) => (
          <div key={i} className={`fade-right d${i + 1}`} style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
            <div style={{ width: 1, minHeight: '100%', background: 'var(--gold)', opacity: 0.6, flexShrink: 0 }} />
            <p style={{ fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 300, color: 'rgba(244,239,230,0.6)', lineHeight: 1.7 }}>{t}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── Methode (3 steps) ── */
function Methode() {
  const steps = [
    { num: '01', title: 'Vous transmettez vos chiffres du mois', text: 'Chiffre d\u2019affaires, charges, achats, flux. Des donn\u00e9es que vous poss\u00e9dez d\u00e9j\u00e0 dans votre comptabilit\u00e9 mensuelle.' },
    { num: '02', title: 'Nous calculons votre position r\u00e9elle', text: 'Notre moteur compare vos dix-huit indicateurs \u00e0 des \u00e9tablissements de m\u00eame profil, m\u00eame secteur, m\u00eame zone. Chaque \u00e9cart est converti en impact financier annuel pr\u00e9cis.' },
    { num: '03', title: 'Vous recevez votre diagnostic personnel', text: 'Un commentaire ancr\u00e9 dans votre histoire. Ce qui tient, ce qui d\u00e9rive, ce qui m\u00e9rite action, et les trois mouvements prioritaires pour le mois \u00e0 venir.' },
  ];

  return (
    <section data-testid="methode-section" style={{ background: '#fff', padding: '120px 64px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, marginBottom: 64 }}>
        <div>
          <Eyebrow text="Notre méthode" />
          <h2 className="fade-up d1" style={{ fontFamily: 'var(--serif)', fontSize: 48, fontWeight: 400, color: 'var(--ink)', lineHeight: 1.12 }}>
            Trois étapes.<br /><em style={{ color: 'var(--gold2)', fontStyle: 'italic' }}>Un diagnostic utilisable demain.</em>
          </h2>
        </div>
        <div className="fade-up d2" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
          <p style={{ fontFamily: 'var(--sans)', fontSize: 13, color: '#777', maxWidth: 240, textAlign: 'right', lineHeight: 1.7 }}>
            Des données que vous avez déjà. Une analyse que vous n'avez jamais eue. Dix minutes par mois, une seule fois.
          </p>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, background: 'var(--sep)' }}>
        {steps.map((s, i) => (
          <div key={s.num} className={`step-card d${i * 2}`} style={{ background: '#fff', padding: '52px 44px' }}>
            <div style={{ fontFamily: 'var(--serif)', fontSize: 88, fontWeight: 400, color: 'var(--ivory2)', lineHeight: 0.9, marginBottom: 28 }}>{s.num}</div>
            <h3 style={{ fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 500, color: 'var(--ink2)', marginBottom: 16 }}>{s.title}</h3>
            <p style={{ fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 300, color: '#777', lineHeight: 1.85 }}>{s.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── Protection des donn\u00e9es (4 cards) ── */
function ProtectionDonnees() {
  const cards = [
    {
      Icon: ShieldCheck,
      title: 'Anonymisation garantie par contrat',
      text: 'Vos chiffres n\u2019apparaissent dans la base comparative que sous forme anonymis\u00e9e. Aucun autre \u00e9tablissement ne peut vous identifier. Cette garantie est inscrite dans vos conditions d\u2019abonnement.',
    },
    {
      Icon: Ban,
      title: 'Z\u00e9ro transmission \u00e0 des tiers',
      text: 'Vos donn\u00e9es ne sont jamais vendues, partag\u00e9es ni transmises \u00e0 un tiers, quelle qu\u2019en soit la raison. Pas \u00e0 des groupements, pas \u00e0 des fournisseurs, pas \u00e0 des cabinets d\u2019analyse.',
    },
    {
      Icon: Trash2,
      title: 'Droit de suppression imm\u00e9diate',
      text: 'Vous pouvez demander la suppression compl\u00e8te de vos donn\u00e9es \u00e0 tout moment, en un message. Elles disparaissent de nos serveurs sous 72 heures.',
    },
    {
      Icon: Scale,
      title: 'Conforme RGPD',
      text: 'BENCHORY respecte le R\u00e8glement G\u00e9n\u00e9ral sur la Protection des Donn\u00e9es. Vos droits d\u2019acc\u00e8s, de rectification et de suppression sont garantis et exer\u00e7ables \u00e0 tout moment.',
    },
  ];

  return (
    <section data-testid="protection-section" style={{ background: 'var(--ivory)', padding: '120px 64px' }}>
      <div style={{ textAlign: 'center', marginBottom: 64 }}>
        <Eyebrow text="Protection de vos données" centered />
        <h2 className="fade-up d1" style={{ fontFamily: 'var(--serif)', fontSize: 48, fontWeight: 400, color: 'var(--ink)', lineHeight: 1.12 }}>
          Vos chiffres vous appartiennent.<br /><em style={{ color: 'var(--gold2)', fontStyle: 'italic' }}>Nous les protégeons.</em>
        </h2>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, background: 'var(--sep)' }}>
        {cards.map((c, i) => (
          <div key={i} className={`fade-up d${i + 1}`} style={{ background: '#fff', padding: '44px 32px' }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--night)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24 }}>
              <c.Icon size={18} color="var(--gold2)" strokeWidth={1.5} />
            </div>
            <h3 style={{ fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 500, color: 'var(--ink2)', marginBottom: 14, lineHeight: 1.4 }}>{c.title}</h3>
            <p style={{ fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 300, color: '#777', lineHeight: 1.85 }}>{c.text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── Citation Fondateur ── */
function Citation() {
  const navigate = useNavigate();
  return (
    <section data-testid="citation-section" style={{ background: 'var(--night)', padding: '140px 64px', textAlign: 'center' }}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <div style={{ fontFamily: 'var(--serif)', fontSize: 88, color: 'var(--gold)', opacity: 0.25, lineHeight: 0.8 }}>«</div>
        <blockquote className="fade-up" style={{ fontFamily: 'var(--serif)', fontSize: 42, fontWeight: 400, fontStyle: 'italic', color: '#F4EFE6', lineHeight: 1.25, maxWidth: 820, margin: '0 auto 36px' }}>
          Nous ne produisons pas des rapports. Nous construisons, mois après mois, la conviction que vous pilotez votre établissement avec lucidité.
        </blockquote>
        <p className="fade-up d1" style={{ fontFamily: 'var(--sans)', fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: 'rgba(244,239,230,0.25)', marginBottom: 48 }}>
          Yovan Lefresne, Fondateur de BENCHORY
        </p>
        <button className="fade-up d2" onClick={() => navigate('/inscription')} style={{
          fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 500, letterSpacing: 3, textTransform: 'uppercase',
          background: 'transparent', color: 'var(--gold)', border: '1px solid var(--gold)', padding: '16px 52px', cursor: 'pointer', transition: 'background 0.25s ease',
        }} onMouseEnter={e => e.target.style.background = 'rgba(180,146,74,0.1)'} onMouseLeave={e => e.target.style.background = 'transparent'}>
          Obtenir mon premier diagnostic
        </button>
        <p className="fade-up d3" style={{ fontFamily: 'var(--sans)', fontSize: 9, color: 'rgba(244,239,230,0.15)', letterSpacing: 2.5, textTransform: 'uppercase', marginTop: 20 }}>
          Premier mois offert · Sans engagement
        </p>
      </div>
    </section>
  );
}

/* ── Tarifs ── */
function Tarifs() {
  const navigate = useNavigate();
  const plans = [
    {
      name: 'Diagnostic', price: '99', dark: false, recommended: false,
      features: [
        'Huit indicateurs essentiels positionn\u00e9s face au march\u00e9',
        'Rapport mensuel personnalis\u00e9 avec commentaire analytique',
        'Chaque \u00e9cart traduit en impact financier annuel',
        'Trois actions prioritaires pour les trente jours \u00e0 venir',
        'Export PDF de chaque diagnostic',
      ],
    },
    {
      name: 'Pilotage', price: '179', dark: true, recommended: true,
      features: [
        'Tout le niveau Diagnostic',
        'Quatorze indicateurs dont productivit\u00e9 et mix commercial',
        'M\u00e9moire illimit\u00e9e et analyse des tendances sur la dur\u00e9e',
        'D\u00e9tection automatique des d\u00e9rives avant qu\u2019elles s\u2019aggravent',
        'Analyse du flux commercial et identification des heures creuses',
      ],
    },
    {
      name: 'Premium', price: '249', dark: false, recommended: false,
      features: [
        'Tout le niveau Pilotage',
        'Dix-huit indicateurs dont tr\u00e9sorerie et tiers payant avanc\u00e9s',
        'Analyse de votre comp\u00e9titivit\u00e9 commerciale \u00e0 l\u2019aide de vos tickets clients',
        'Acc\u00e8s prioritaire \u00e0 l\u2019\u00e9quipe BENCHORY',
        'Accompagnement sur mesure \u00e0 chaque \u00e9tape',
      ],
    },
  ];

  return (
    <section id="tarifs" data-testid="tarifs-section" style={{ background: '#fff', padding: '120px 64px' }}>
      <div style={{ textAlign: 'center', marginBottom: 64 }}>
        <Eyebrow text="Nos tarifs" centered />
        <h2 className="fade-up d1" style={{ fontFamily: 'var(--serif)', fontSize: 48, fontWeight: 400, color: 'var(--ink)', lineHeight: 1.12 }}>
          Trois niveaux.<br /><em style={{ color: 'var(--gold2)', fontStyle: 'italic' }}>Un engagement : la clarté.</em>
        </h2>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, background: 'var(--sep)' }}>
        {plans.map((p, i) => {
          const bg = p.dark ? 'var(--night)' : '#fff';
          const txt = p.dark ? '#F4EFE6' : 'var(--ink)';
          const sub = p.dark ? 'rgba(244,239,230,0.4)' : '#777';
          const sep = p.dark ? 'rgba(255,255,255,0.08)' : 'var(--sep)';
          return (
            <div key={p.name} className={`plan-card d${i * 2}`} style={{ background: bg, padding: '52px 40px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontFamily: 'var(--sans)', fontSize: 9, letterSpacing: 4, textTransform: 'uppercase', color: 'var(--gold)' }}>{p.name}</span>
                {p.recommended && <span style={{ fontFamily: 'var(--sans)', fontSize: 7, letterSpacing: 2, textTransform: 'uppercase', background: 'var(--gold)', color: 'var(--night)', padding: '3px 8px', fontWeight: 500 }}>recommandé</span>}
              </div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 60, fontWeight: 400, color: txt, lineHeight: 1, margin: '8px 0' }}>
                {p.price}<span style={{ fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 300, color: sub, marginLeft: 6 }}>€ HT / mois</span>
              </div>
              <div style={{ width: '100%', height: 1, background: sep, margin: '24px 0' }} />
              <div style={{ flex: 1 }}>
                {p.features.map((f, fi) => (
                  <div key={fi} style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'flex-start' }}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--gold)', flexShrink: 0, marginTop: 7, opacity: 0.7 }} />
                    <span style={{ fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 300, color: sub, lineHeight: 1.6 }}>{f}</span>
                  </div>
                ))}
              </div>
              <button data-testid={`tarifs-cta-${p.name.toLowerCase()}`} onClick={() => navigate('/inscription')} style={{
                fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 500, letterSpacing: 3, textTransform: 'uppercase',
                padding: '16px 0', width: '100%', cursor: 'pointer', marginTop: 32, transition: 'all 0.25s ease',
                ...(p.dark
                  ? { background: 'var(--gold2)', color: 'var(--night)', border: 'none' }
                  : { background: 'transparent', color: 'var(--ink)', border: '1px solid var(--ink)' }
                ),
              }} onMouseEnter={e => {
                if (p.dark) { e.target.style.background = 'var(--gold3)'; }
                else { e.target.style.background = 'var(--night)'; e.target.style.color = 'white'; }
              }} onMouseLeave={e => {
                if (p.dark) { e.target.style.background = 'var(--gold2)'; }
                else { e.target.style.background = 'transparent'; e.target.style.color = 'var(--ink)'; }
              }}>
                {p.recommended ? 'Commencer avec Pilotage' : `Choisir ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ── FAQ Accordion ── */
function FAQ() {
  const [open, setOpen] = useState(null);
  const items = [
    { q: 'Comment fonctionne le premier mois offert\u00a0?', a: 'Vous acc\u00e9dez \u00e0 l\u2019int\u00e9gralit\u00e9 de votre niveau pendant trente jours sans frais. \u00c0 la fin du premier mois, vous choisissez de continuer ou d\u2019arr\u00eater, sans aucune obligation ni d\u00e9marche particuli\u00e8re.' },
    { q: 'Mes donn\u00e9es sont-elles confidentielles\u00a0?', a: 'Enti\u00e8rement. Vos donn\u00e9es sont chiffr\u00e9es, strictement anonymis\u00e9es dans notre base comparative, et ne sont jamais transmises \u00e0 des tiers. Aucun autre \u00e9tablissement ne peut acc\u00e9der \u00e0 vos chiffres.' },
    { q: 'Faut-il connecter mon logiciel de gestion\u00a0?', a: 'Non. BENCHORY ne n\u00e9cessite aucune connexion \u00e0 votre logiciel de caisse ou de gestion. Vous saisissez chaque mois une vingtaine de donn\u00e9es issues de votre comptabilit\u00e9, des chiffres que vous poss\u00e9dez d\u00e9j\u00e0. La saisie prend moins de dix minutes.' },
    { q: 'Puis-je changer de niveau en cours d\u2019abonnement\u00a0?', a: 'Oui. Vous pouvez \u00e9voluer vers un niveau sup\u00e9rieur \u00e0 tout moment, avec effet imm\u00e9diat. Le passage \u00e0 un niveau inf\u00e9rieur prend effet au prochain cycle mensuel.' },
    { q: 'Pour quels types d\u2019\u00e9tablissements BENCHORY est-il con\u00e7u\u00a0?', a: 'BENCHORY est aujourd\u2019hui sp\u00e9cialis\u00e9 pour les pharmacies d\u2019officine fran\u00e7aises. D\u2019autres secteurs (restaurants, opticiens, commerces de proximit\u00e9) seront disponibles prochainement.' },
  ];

  return (
    <section data-testid="faq-section" style={{ background: 'var(--ivory)', padding: '100px 64px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 80 }}>
        <div>
          <Eyebrow text="Questions fréquentes" />
          <h2 className="fade-up d1" style={{ fontFamily: 'var(--serif)', fontSize: 48, fontWeight: 400, color: 'var(--ink)', lineHeight: 1.12, marginBottom: 20 }}>
            Tout ce que vous<br /><em style={{ color: 'var(--gold2)', fontStyle: 'italic' }}>souhaitez savoir.</em>
          </h2>
          <p className="fade-up d2" style={{ fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 300, color: '#777', lineHeight: 1.7 }}>
            Nous répondons à vos questions les plus fréquentes. Pour toute autre demande, notre équipe vous répond sous vingt-quatre heures ouvrables.
          </p>
        </div>
        <div>
          {items.map((item, i) => (
            <div key={i} style={{ borderTop: '1px solid #E0D4CF', ...(i === items.length - 1 ? { borderBottom: '1px solid #E0D4CF' } : {}) }}>
              <button data-testid={`faq-q-${i}`} onClick={() => setOpen(open === i ? null : i)} style={{
                width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '20px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
              }}>
                <span style={{ fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 500, color: 'var(--ink)', transition: 'color 0.25s ease' }}
                  onMouseEnter={e => e.target.style.color = 'var(--gold)'} onMouseLeave={e => e.target.style.color = 'var(--ink)'}>{item.q}</span>
                <span className={`faq-icon ${open === i ? 'open' : ''}`} style={{ fontFamily: 'var(--serif)', fontSize: 22, color: 'var(--gold)', flexShrink: 0, marginLeft: 16 }}>+</span>
              </button>
              <div className={`faq-answer ${open === i ? 'open' : ''}`}>
                <p style={{ fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 300, color: '#777', lineHeight: 1.85, paddingRight: 40 }}>{item.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Contact Section ── */
function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setSent(true);
    } catch {
      setSent(true);
    }
    setSending(false);
  };

  return (
    <section id="contact" data-testid="contact-section" style={{ background: 'var(--night)', padding: '120px 64px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 80, maxWidth: 1100, margin: '0 auto' }}>
        <div>
          <Eyebrow text="Contact" />
          <h2 className="fade-up d1" style={{ fontFamily: 'var(--serif)', fontSize: 48, fontWeight: 400, color: '#F4EFE6', lineHeight: 1.12, marginBottom: 24 }}>
            Une question ?<br /><em style={{ color: 'var(--gold2)', fontStyle: 'italic' }}>Contactez-nous.</em>
          </h2>
          <p className="fade-up d2" style={{ fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 300, color: 'rgba(244,239,230,0.35)', lineHeight: 1.8, marginBottom: 32 }}>
            Notre équipe vous répond sous vingt-quatre heures ouvrables. Que vous ayez une question sur nos offres, notre méthode ou une demande particulière, nous sommes à votre écoute.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--gold)', opacity: 0.7 }} />
              <span style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'rgba(244,239,230,0.4)' }}>contact@benchory.fr</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--gold)', opacity: 0.7 }} />
              <span style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'rgba(244,239,230,0.4)' }}>Réponse sous 24h ouvrables</span>
            </div>
          </div>
        </div>

        <div className="fade-up d2">
          {sent ? (
            <div style={{ background: 'rgba(180,146,74,0.08)', border: '1px solid rgba(180,146,74,0.2)', padding: '52px 40px', textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 36, color: 'var(--gold2)', marginBottom: 16 }}>Merci</div>
              <p style={{ fontFamily: 'var(--sans)', fontSize: 14, color: 'rgba(244,239,230,0.45)', lineHeight: 1.7 }}>
                Votre message a bien été envoyé. Nous revenons vers vous très rapidement.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontFamily: 'var(--sans)', fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: 8 }}>Nom</label>
                  <input data-testid="contact-nom" required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#F4EFE6', fontFamily: 'var(--sans)', fontSize: 14, padding: '14px 16px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontFamily: 'var(--sans)', fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: 8 }}>Email</label>
                  <input data-testid="contact-email" required type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                    style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#F4EFE6', fontFamily: 'var(--sans)', fontSize: 14, padding: '14px 16px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ fontFamily: 'var(--sans)', fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: 8 }}>Téléphone (optionnel)</label>
                <input data-testid="contact-tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#F4EFE6', fontFamily: 'var(--sans)', fontSize: 14, padding: '14px 16px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ fontFamily: 'var(--sans)', fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--gold)', display: 'block', marginBottom: 8 }}>Message</label>
                <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: '#F4EFE6', fontFamily: 'var(--sans)', fontSize: 14, padding: '14px 16px', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
              <button data-testid="contact-submit" type="submit" disabled={sending} style={{
                fontFamily: 'var(--sans)', fontSize: 10, fontWeight: 500, letterSpacing: 3, textTransform: 'uppercase',
                background: 'var(--gold2)', color: 'var(--night)', border: 'none', padding: '17px 40px', cursor: 'pointer',
                transition: 'background 0.25s ease', alignSelf: 'flex-start', opacity: sending ? 0.6 : 1,
              }} onMouseEnter={e => e.target.style.background = 'var(--gold3)'} onMouseLeave={e => e.target.style.background = 'var(--gold2)'}>
                {sending ? 'Envoi en cours...' : 'Envoyer'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

/* ── Qui sommes-nous ── */
function QuiSommesNous() {
  const founders = [
    {
      name: 'Yovan Lefresne',
      role: 'Fondateur',
      bio: '\u00c0 21 ans apr\u00e8s une classe pr\u00e9paratoire, Yovan Lefresne fonde BENCHORY pour une raison pr\u00e9cise\u00a0: les grandes enseignes ont des directeurs financiers, des outils d\u2019analyse, des \u00e9quipes d\u00e9di\u00e9es \u00e0 la performance. Les ind\u00e9pendants ont leur instinct. Ce d\u00e9s\u00e9quilibre semblait injuste. BENCHORY est n\u00e9 de cette conviction que chaque dirigeant, quelle que soit la taille de son \u00e9tablissement, m\u00e9rite de piloter avec les m\u00eames certitudes.',
    },
    {
      name: 'Nicolas Pecault',
      role: 'Co-fondateur',
      bio: 'Nicolas Pecault a 23 ans et termine son master \u00e0 l\u2019EM Lyon lorsqu\u2019il d\u00e9couvre BENCHORY. Il rejoint le projet convaincu par une id\u00e9e simple\u00a0: qu\u2019un dirigeant ind\u00e9pendant m\u00e9rite mieux qu\u2019un bilan annuel sans contexte. Il contribue aujourd\u2019hui \u00e0 renforcer ce qui est au c\u0153ur de BENCHORY\u00a0: la qualit\u00e9 de l\u2019analyse, et sa capacit\u00e9 \u00e0 v\u00e9ritablement \u00e9clairer une d\u00e9cision.',
    },
  ];

  return (
    <section data-testid="qui-sommes-nous-section" style={{ background: 'var(--ivory)', padding: '120px 64px' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 72 }}>
          <Eyebrow text="Qui sommes-nous" centered />
          <h2 className="fade-up d1" style={{ fontFamily: 'var(--serif)', fontSize: 48, fontWeight: 400, color: 'var(--ink)', lineHeight: 1.12 }}>
            Deux convictions.<br /><em style={{ color: 'var(--gold2)', fontStyle: 'italic' }}>Un même engagement.</em>
          </h2>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
          {founders.map((f, i) => (
            <div key={f.name} className={`fade-up d${i + 1}`} style={{ background: '#fff', padding: '52px 48px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
                <div style={{
                  width: 52, height: 52, borderRadius: '50%', background: 'var(--night)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'var(--serif)', fontSize: 20, color: 'var(--gold2)', fontWeight: 400,
                }}>
                  {f.name.split(' ').map(w => w[0]).join('')}
                </div>
                <div>
                  <div style={{ fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 500, color: 'var(--ink)', marginBottom: 2 }}>{f.name}</div>
                  <div style={{ fontFamily: 'var(--sans)', fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--gold)' }}>{f.role}</div>
                </div>
              </div>
              <div style={{ width: 32, height: 1, background: 'var(--gold)', opacity: 0.4, marginBottom: 24 }} />
              <p style={{ fontFamily: 'var(--sans)', fontSize: 14, fontWeight: 300, color: '#555', lineHeight: 1.9 }}>
                {f.bio}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── Footer ── */
function Footer() {
  return (
    <footer data-testid="footer" style={{ background: 'var(--night2)', padding: '60px 64px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <img src={LOGO_URL} alt="BENCHORY" style={{ height: 24, objectFit: 'contain', opacity: 0.4 }} />
      </div>
      <div style={{ display: 'flex', gap: 28 }}>
        {[
          { label: 'Mentions l\u00e9gales', to: '/mentions-legales' },
          { label: 'CGU', to: '/cgu' },
          { label: 'CGV', to: '/cgv' },
          { label: 'Confidentialit\u00e9', to: '/confidentialite' },
          { label: 'contact@benchory.fr', to: 'mailto:contact@benchory.fr' },
        ].map(l => (
          l.to.startsWith('mailto:') ? (
            <a key={l.label} href={l.to} style={{
              fontFamily: 'var(--sans)', fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(244,239,230,0.18)', textDecoration: 'none', transition: 'color 0.2s ease',
            }} onMouseEnter={e => e.target.style.color = 'var(--gold)'} onMouseLeave={e => e.target.style.color = 'rgba(244,239,230,0.18)'}>{l.label}</a>
          ) : (
            <Link key={l.label} to={l.to} style={{
              fontFamily: 'var(--sans)', fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(244,239,230,0.18)', textDecoration: 'none', transition: 'color 0.2s ease',
            }} onMouseEnter={e => e.target.style.color = 'var(--gold)'} onMouseLeave={e => e.target.style.color = 'rgba(244,239,230,0.18)'}>{l.label}</Link>
          )
        ))}
      </div>
      <span style={{ fontFamily: 'var(--sans)', fontSize: 9, letterSpacing: 1, color: 'rgba(244,239,230,0.12)' }}>© 2026 BENCHORY</span>
    </footer>
  );
}

/* ── Helper: Eyebrow ── */
function Eyebrow({ text, centered }) {
  return (
    <div className="fade-up" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, ...(centered ? { justifyContent: 'center' } : {}) }}>
      {!centered && <span style={{ width: 28, height: 1, background: 'var(--gold)', opacity: 0.5, display: 'inline-block' }} />}
      <span style={{ fontFamily: 'var(--sans)', fontSize: 9, letterSpacing: 5, textTransform: 'uppercase', color: 'var(--gold)' }}>{text}</span>
      {!centered && <span style={{ width: 28, height: 1, background: 'var(--gold)', opacity: 0.5, display: 'inline-block' }} />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN LANDING PAGE
   ═══════════════════════════════════════════════════════════════════ */
export default function LandingPage() {
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
    }, { threshold: 0.15 });
    document.querySelectorAll('.fade-up, .fade-left, .fade-right, .step-card, .plan-card, .srow').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div style={{ fontFamily: 'var(--sans)', color: 'var(--ink)', overflowX: 'hidden' }}>
      <style>{CSS_VARS}</style>
      <Navbar />
      <Hero />
      <Ticker />
      <Conviction />
      <Split />
      <Methode />
      <ProtectionDonnees />
      <Citation />
      <Tarifs />
      <FAQ />
      <ContactSection />
      <QuiSommesNous />
      <Footer />
    </div>
  );
}
